import { 
    Alert, 
    Button, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogContentText, 
    DialogTitle, 
    Grid,
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    List,
    ListItem,
    ListItemText,
    Divider
} from "@mui/material";
import { useGenericMutation } from "../../services/react-query/generic.mutation";
import Papa from "papaparse";
import React, { useRef, useState } from "react";
import { Dropzone } from "../dropzone";
import { toast } from "react-toastify";
import { LoadingButton } from "@mui/lab";
import { handleApiError } from "../../services/react-query/react-query.client";
import Loading from "../loading";
import { When } from "react-if";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PaginationFactory } from "../../factory";
import { UserTypes } from "../../types";
import { UserService } from "../../services/user.service";

interface Props {
    visible: boolean;
    onClose: () => void;
    toggleRefresh: boolean;
    setToggleRefresh: (flag: boolean) => void;
    uniqueIdentifier?: string;
    isCustomerSettingsLoading?: boolean;
}

type AssignmentRow = {
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    backupEmail?: string;
    employeeId?: string;
    groupName: string;
    teamName: string;
};

type PreviewResponse = {
    summary: {
        usersToAssign: number;
        newGroupsCount: number;
        newTeamsCount: number;
    };
    details: {
        newGroupsUsed: string[];
        newTeamsUsed: Array<{ groupName: string; teamName: string; }>;
        existingGroupsUsed: string[];
        existingTeamsUsed: Array<{ groupName: string; teamName: string; }>;
    };
    warnings: {
        usersNotFound: Array<AssignmentRow>;
        duplicateAssignments: string[];
    };
    info: {
        customerUserUniqueIdentifier: string;
    }
};

export const ImportGroupsTeamsModal: React.FC<Props> = ({
    visible,
    onClose,
    toggleRefresh,
    setToggleRefresh,
    uniqueIdentifier,
    isCustomerSettingsLoading,
}) => {
    // States
    const [error, setError] = useState<string | null>(null);
    const [file64, setFile64] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File>();
    const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
    const [previewData, setPreviewData] = useState<PreviewResponse | null>(null);
    const [previewExpanded, setPreviewExpanded] = useState<boolean>(false);
    const previewRef = useRef<HTMLDivElement>(null);

    // Mutations
    const {
        mutateAsync: previewMutation,
        isLoading: isPreviewLoading,
    } = useGenericMutation<{ assignments: AssignmentRow[] }, PreviewResponse>(
        'previewGroupsTeamsImport',
        `users/groups-teams/preview`,
        'post'
    );

    const {
        mutateAsync: importMutation,
        isLoading: isImportLoading,
    } = useGenericMutation<{ assignments: AssignmentRow[] }, {
        groupsCreated: number;
        teamsCreated: number;
        usersAssigned: number;
        usersSkipped: number;
        errors: string[];
    }>(
        'importGroupsTeams',
        `users/groups-teams/import`,
        'post'
    );

    // Functions
    const parseCSV = async (file: File) => {
        Papa.parse(file, {
            header: true,
            transformHeader: (header) => header.trim().toLowerCase().replace(/[^a-z]/g, ''),
            complete: async (result: any) => {
                const data = result.data as any[];

                if (data.length > 0) {
                    const sanitizedData: Array<AssignmentRow> = [];

                    data?.forEach((d) => {
                        if (d.firstname && d.lastname && checkForUniqueFieldFlag(d)) {
                            // Sanitize the optional fields
                            const sanitizedBackupEmail = d.backupemail && d.backupemail.trim() !== '' ? d.backupemail : undefined;
                            const sanitizedEmployeeId = d.employeeid && d.employeeid.trim() !== '' ? d.employeeid : undefined;
                            const sanitizedPhoneNumber = d.phonenumber && d.phonenumber.trim() !== '' ? d.phonenumber : undefined;

                            // Only add if groupName and teamName are present
                            if (d.groupname && d.teamname) {
                                sanitizedData.push({
                                    firstName: d.firstname,
                                    lastName: d.lastname,
                                    phoneNumber: sanitizedPhoneNumber,
                                    backupEmail: sanitizedBackupEmail,
                                    employeeId: sanitizedEmployeeId,
                                    groupName: d.groupname,
                                    teamName: d.teamname,
                                });
                            }
                        }
                    });

                    setAssignments(sanitizedData);

                    if (sanitizedData.length === 0) {
                        toast(
                            'Error: no assignment information pulled from the file, check the formatting',
                            {
                                autoClose: 5000,
                                type: 'error',
                            }
                        );
                        setPreviewData(null);
                    } else {
                        // Call preview endpoint
                        await fetchPreview(sanitizedData);
                    }
                }
            },
            error: (error: any) => {
                toast(
                    'An error occurred while trying to import from the file, format is invalid',
                    {
                        autoClose: 1500,
                        type: 'error',
                    }
                );
            },
        });
    };

    const fetchPreview = async (assignmentsData: AssignmentRow[]) => {
        try {
            const result = await previewMutation({ assignments: assignmentsData });
            setPreviewData(result.result);
            setPreviewExpanded(true);

            // Auto-scroll to preview after a short delay
            setTimeout(() => {
                previewRef.current?.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'nearest' 
                });
            }, 100);
        } catch (err) {
            const msg = handleApiError(err);
            toast.error(`Preview failed: ${msg}`);
            setPreviewData(null);
        }
    };

    const checkForUniqueFieldFlag = (userData: any): boolean => {
        if (!uniqueIdentifier || uniqueIdentifier === "INVITECODE")
            return !!userData.phonenumber;
        else if (uniqueIdentifier === "EMPLOYEEID")
            return !!userData.employeeid;
        else if (uniqueIdentifier === "EMAILADDRESS")
            return !!userData.backupemail;
        return true;
    };

    const getNoteText = (): string => {
        if (!uniqueIdentifier || uniqueIdentifier === "INVITECODE")
            return "NOTE: firstName, lastName, phoneNumber, groupName, and teamName are required fields. Other fields are optional";
        else if (uniqueIdentifier === "EMAILADDRESS")
            return "NOTE: firstName, lastName, backupEmail, groupName, and teamName are required fields. Other fields are optional";
        else if (uniqueIdentifier === "EMPLOYEEID")
            return "NOTE: firstName, lastName, employeeId, groupName, and teamName are required fields. Other fields are optional";
        return "NOTE: firstName, lastName, groupName, and teamName are required fields. Other fields are optional";
    };

    const onSubmit = async () => {
        await importMutation({ assignments }, {
            onError: (err) => {
                const msg = handleApiError(err);
                setError(msg);
                toast.error(`Import failed: ${msg}`);
            },
            onSuccess: (result) => {
                setFile64(null);
                setSelectedFile(undefined);
                setAssignments([]);
                setPreviewData(null);
                setPreviewExpanded(false);
                onClose();
                setToggleRefresh(!toggleRefresh);

                if (result.result.usersAssigned === 0) {
                    toast.info('No users assigned, no valid assignments found in file');
                } else {
                    const message = `Successfully imported! ${result.result.usersAssigned} users assigned, ${result.result.groupsCreated} groups created, ${result.result.teamsCreated} teams created.`;
                    
                    if (result.result.errors.length > 0) {
                        toast.warning(`${message} However, some errors occurred. Check console for details.`);
                        console.error('Import errors:', result.result.errors);
                    } else {
                        toast.success(message);
                    }
                }
            },
        });
    };

    const hasWarnings = previewData && (
        previewData.warnings.usersNotFound.length > 0 ||
        previewData.warnings.duplicateAssignments.length > 0
    );

    // Rendering
    return (
        <Dialog
            scroll={'paper'}
            onClose={onClose}
            open={visible}
            maxWidth="md"
            fullWidth
        >
            <When condition={isCustomerSettingsLoading}>
                <Loading />
            </When>
            <When condition={!isCustomerSettingsLoading}>
                <DialogTitle fontWeight={500} mb={1} id="alert-dialog-title">
                    Import Groups and Teams
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Upload a .csv file to assign users to groups and teams. This will create new groups and teams as needed.
                    </DialogContentText>
                    <Grid display={'flex'} alignItems={'center'} justifyItems={'center'} alignSelf={'center'} flexDirection={'row'} spacing={3} mb={3}>
                        <Button
                            size="small"
                            variant="text"
                            sx={{ mt: 2 }}
                            onClick={async () => {
                                try {
                                    // Fetch all active users
                                    const activeUsersRes: PaginationFactory<UserTypes.Model> = await UserService.fetchMany(
                                        { perPage: 100000, page: 1, filter: {}, sort: {} },
                                        undefined,
                                        true,
                                        true, // active users
                                    ) as PaginationFactory<UserTypes.Model>;
                                    // Fetch all inactive users
                                    const inactiveUsersRes: PaginationFactory<UserTypes.Model> = await UserService.fetchMany(
                                        { perPage: 100000, page: 1, filter: {}, sort: {} },
                                        undefined,
                                        true,
                                        false, // inactive users
                                    ) as PaginationFactory<UserTypes.Model>;

                                    // Build CSV array
                                    const csvArray = [
                                        // Example row
                                        {
                                            firstName: 'EXAMPLE',
                                            lastName: 'USER',
                                            phoneNumber: '+19998887777',
                                            backupEmail: 'example@pomsafe.com',
                                            employeeId: '123456',
                                            groupName: 'Massachusetts',
                                            teamName: 'Home Health',
                                        },
                                        // Active users
                                        ...(activeUsersRes.items?.map((user) => ({
                                            firstName: user?.firstName ?? '',
                                            lastName: user?.lastName ?? '',
                                            phoneNumber: user?.phoneNumber ?? '',
                                            backupEmail: user?.backupEmail ?? '',
                                            employeeId: user?.employeeId ?? '',
                                            groupName: '',
                                            teamName: '',
                                        })) || []),
                                        // Inactive users
                                        ...(inactiveUsersRes.items?.map((user) => ({
                                            firstName: user?.firstName ?? '',
                                            lastName: user?.lastName ?? '',
                                            phoneNumber: user?.phoneNumber ?? '',
                                            backupEmail: user?.backupEmail ?? '',
                                            employeeId: user?.employeeId ?? '',
                                            groupName: '',
                                            teamName: '',
                                        })) || []),
                                    ];

                                    const csv = Papa.unparse(csvArray);

                                    const element = document.createElement('a');
                                    const file = new Blob([csv], { type: 'text/csv' });
                                    const url = URL.createObjectURL(file);
                                    element.href = url;
                                    element.download = 'import-groups-teams-template.csv';
                                    document.body.appendChild(element);
                                    element.click();

                                    // Cleanup after download
                                    document.body.removeChild(element);
                                    URL.revokeObjectURL(url);
                                } catch (error) {
                                    toast.error('Failed to generate CSV template');
                                    console.error('CSV generation error:', error);
                                }
                            }}
                        >
                            Download csv template
                        </Button>
                    </Grid>

                    {error &&
                        <Alert variant="standard" color="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    }

                    <Dropzone
                        file={file64 || undefined}
                        selectedFile={selectedFile}
                        onChange={(b64, file) => {
                            if (file.type !== 'text/csv') {
                                toast(
                                    'Invalid file type, should be a .csv file.',
                                    {
                                        autoClose: 1500,
                                        type: 'error',
                                    }
                                );
                                return;
                            }
                            setSelectedFile(file);
                            setFile64(b64);
                            parseCSV(file);
                        }}
                    />

                    {/* Preview Section */}
                    {isPreviewLoading && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Loading />
                            <Typography variant="body2" color="text.secondary">
                                Analyzing CSV file...
                            </Typography>
                        </Box>
                    )}

                    {previewData && (
                        <Accordion
                            ref={previewRef} 
                            expanded={previewExpanded} 
                            onChange={() => setPreviewExpanded(!previewExpanded)}
                            sx={{ mt: 2 }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    {hasWarnings ? (
                                        <WarningAmberIcon color="warning" />
                                    ) : (
                                        <CheckCircleOutlineIcon color="success" />
                                    )}
                                    <Typography fontWeight={500}>
                                        Import Preview
                                    </Typography>
                                    {hasWarnings && (
                                        <Chip 
                                            label="Warnings" 
                                            color="warning" 
                                            size="small" 
                                            sx={{ ml: 1 }}
                                        />
                                    )}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {/* Summary */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                        Summary
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={4}>
                                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                                <Typography variant="h4" color="primary">
                                                    {previewData.summary.usersToAssign}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Users to Assign
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                                <Typography variant="h4" color="success.main">
                                                    {previewData.summary.newGroupsCount}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    New Groups
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                                <Typography variant="h4" color="success.main">
                                                    {previewData.summary.newTeamsCount}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    New Teams
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                {/* Warnings */}
                                {hasWarnings && (
                                    <Box sx={{ mb: 3 }}>
                                        <Alert severity="warning" icon={<WarningAmberIcon />}>
                                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                Warnings - Please Review
                                            </Typography>
                                            
                                            {previewData.warnings.usersNotFound.length > 0 && (
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        Users Not Found ({previewData.warnings.usersNotFound.length}):
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        The following users don't exist in the system and will be skipped:
                                                    </Typography>
                                                    <List dense>
                                                        {previewData.warnings.usersNotFound.slice(0, 5).map((user, idx) => (
                                                            <ListItem key={idx} sx={{ py: 0.5 }}>
                                                                <ListItemText 
                                                                    primary={`${user.firstName} ${user.lastName}`}
                                                                    secondary={user.phoneNumber || user.backupEmail || user.employeeId}
                                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                                    secondaryTypographyProps={{ variant: 'caption' }}
                                                                />
                                                            </ListItem>
                                                        ))}
                                                        {previewData.warnings.usersNotFound.length > 5 && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                                                ...and {previewData.warnings.usersNotFound.length - 5} more
                                                            </Typography>
                                                        )}
                                                    </List>
                                                </Box>
                                            )}

                                            {previewData.warnings.duplicateAssignments.length > 0 && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        Duplicate Users ({previewData.warnings.duplicateAssignments.length}):
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        The following users appear multiple times. The last assignment will be used:
                                                    </Typography>
                                                    <List dense>
                                                        {previewData.warnings.duplicateAssignments.slice(0, 5).map((identifier, idx) => (
                                                            <ListItem key={idx} sx={{ py: 0.5 }}>
                                                                <ListItemText 
                                                                    primary={identifier}
                                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                                />
                                                            </ListItem>
                                                        ))}
                                                        {previewData.warnings.duplicateAssignments.length > 5 && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                                                ...and {previewData.warnings.duplicateAssignments.length - 5} more
                                                            </Typography>
                                                        )}
                                                    </List>
                                                </Box>
                                            )}
                                            
                                            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                                                You can still proceed with the import, but these issues should be reviewed.
                                            </Typography>
                                        </Alert>
                                    </Box>
                                )}

                                {/* New Groups */}
                                {previewData.details.newGroupsUsed.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                            New Groups to be Created ({previewData.details.newGroupsUsed.length})
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {previewData.details.newGroupsUsed.map((group, idx) => (
                                                <Chip key={idx} label={group} color="success" variant="outlined" size="small" />
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                {/* New Teams */}
                                {previewData.details.newTeamsUsed.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                            New Teams to be Created ({previewData.details.newTeamsUsed.length})
                                        </Typography>
                                        <List dense>
                                            {previewData.details.newTeamsUsed.map((team, idx) => (
                                                <ListItem key={idx} sx={{ py: 0.5 }}>
                                                    <ListItemText 
                                                        primary={team.teamName}
                                                        secondary={`in ${team.groupName}`}
                                                        primaryTypographyProps={{ variant: 'body2' }}
                                                        secondaryTypographyProps={{ variant: 'caption' }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}

                                {/* Existing Groups Used */}
                                {previewData.details.existingGroupsUsed.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                            Existing Groups ({previewData.details.existingGroupsUsed.length})
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {previewData.details.existingGroupsUsed.map((group, idx) => (
                                                <Chip key={idx} label={group} variant="outlined" size="small" />
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                {/* Existing Teams Used */}
                                {previewData.details.existingTeamsUsed.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                            Existing Teams ({previewData.details.existingTeamsUsed.length})
                                        </Typography>
                                        <List dense>
                                            {previewData.details.existingTeamsUsed.map((team, idx) => (
                                                <ListItem key={idx} sx={{ py: 0.5 }}>
                                                    <ListItemText 
                                                        primary={team.teamName}
                                                        secondary={`in ${team.groupName}`}
                                                        primaryTypographyProps={{ variant: 'body2' }}
                                                        secondaryTypographyProps={{ variant: 'caption' }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}

                                {/* Info */}
                                <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 2 }}>
                                    <Typography variant="body2">
                                        Using <strong>{previewData.info.customerUserUniqueIdentifier}</strong> as the unique identifier for matching users.
                                    </Typography>
                                </Alert>
                            </AccordionDetails>
                        </Accordion>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button disabled={isImportLoading} color="inherit" onClick={onClose}>
                        Cancel
                    </Button>
                    <LoadingButton 
                        onClick={onSubmit} 
                        loading={isImportLoading} 
                        disabled={assignments.length === 0 || !previewData} 
                        autoFocus
                    >
                        Confirm Import
                    </LoadingButton>
                </DialogActions>
            </When>
        </Dialog>
    );
};